import {
  API_USERS_BACKEND,
  API_SERVICEPROVIDERS_BACKEND,
} from "./api";
import { bitsToRoleAssignments } from "../utils/bitsToRoleAssignments";
import { isSuperAdmin, isAnyMsb } from "../utils/msb_role";
import type {
  UserResponse as BackendUserResponse,
  ClientAssignment as BackendClientAssignment,
  GetElectronicSignaturesResponse,
  DistrictRef,
  GetCaseloadQueryResponse,
  ProviderTypeRecord,
  ServiceProviderRecord,
} from "@xng/xng-backend";
import { CaseloadType } from "@xng/xng-backend";
import type {
  UserResponse as OldUserResponse,
  ClientAssignment as OldClientAssignment,
  ElectronicSignature as OldElectronicSignature,
  ServiceProviderType as OldServiceProviderType,
} from "../profile-sdk";

/**
 * Assembles an old-shaped UserResponse from the decomposed backend APIs.
 * Pure async function — no hooks, no dispatch. Callable from anywhere.
 */
export async function fetchUserResponse(
  userId: string,
  emailAddress?: string,
): Promise<OldUserResponse> {
  const backendUser = await API_USERS_BACKEND.getUser({
    id: userId,
    emailAddress,
  });
  return assembleUserResponse(backendUser);
}

/**
 * Variant for the "login as another user" cookie flow.
 */
export async function fetchUserResponseByServiceProvider(
  serviceProviderId: string,
  clientId: string,
): Promise<OldUserResponse> {
  const backendUser = await API_USERS_BACKEND.getUserByServiceProvider({
    serviceProviderId,
    clientId,
  });
  return assembleUserResponse(backendUser);
}

// ── internal ────────────────────────────────────────────────────────────

async function assembleUserResponse(
  backendUser: BackendUserResponse,
): Promise<OldUserResponse> {
  if (!backendUser.id) throw new Error("Backend user has no id");

  const [backendAssignments, backendSignatures, backendProviderTypes] = await Promise.all([
    API_USERS_BACKEND
      .getClientAssignments({ id: backendUser.id })
      .catch(() => [] as BackendClientAssignment[]),
    API_USERS_BACKEND
      .getElectronicSignatures({ id: backendUser.id })
      .catch(() => [] as GetElectronicSignaturesResponse[]),
    API_USERS_BACKEND
      .getProviderTypes({ id: backendUser.id })
      .catch(() => [] as ProviderTypeRecord[]),
  ]);

  const activeClientId =
    backendUser.loggedInClientId ??
    backendAssignments.find((ba) => ba.status === 1)?.client?.id;

  const shimmedAssignments = await buildClientAssignments(backendAssignments, activeClientId);
  const shimmedSignatures = buildElectronicSignatures(backendSignatures);
  const shimmedProviderTypes = buildServiceProviderTypes(backendProviderTypes);

  return {
    id: backendUser.id,
    firstName: backendUser.firstName,
    lastName: backendUser.lastName,
    emailAddress: backendUser.emailAddress,
    dateOfBirth: backendUser.dateOfBirth as any,
    isSuperAdmin: isSuperAdmin(backendUser),
    isMsbAdmin: isAnyMsb(backendUser),
    loggedInClientId: backendUser.loggedInClientId ?? undefined,
    serviceProviderTypes: shimmedProviderTypes,
    clientAssignments: shimmedAssignments,
    electronicSignatures: shimmedSignatures,
  };
}

async function buildClientAssignments(
  backendAssignments: BackendClientAssignment[],
  loggedInClientId?: string | null,
): Promise<OldClientAssignment[]> {
  return Promise.all(
    backendAssignments.map(async (ba) => {
      const roles = bitsToRoleAssignments(ba.roles as number | undefined);
      const isActiveClient = ba.client?.id != null && ba.client.id === loggedInClientId;

      // Only fetch heavy data (SP record, districts, caseloads) for the active client.
      // Non-active assignments only need the lightweight fields for the client-switcher dropdown.
      let spRecord: ServiceProviderRecord | undefined;
      let districts: DistrictRef[] = [];
      if (isActiveClient && ba.serviceProviderId) {
        try {
          [spRecord, districts] = await Promise.all([
            API_SERVICEPROVIDERS_BACKEND
              .getServiceProvider({
                serviceProviderId: ba.serviceProviderId,
              }),
            API_SERVICEPROVIDERS_BACKEND
              .getServiceProviderDistricts({
                serviceProviderId: ba.serviceProviderId,
              }),
          ]);
        } catch {
          districts = [];
        }
      }

      let supervisedProviders: GetCaseloadQueryResponse[] = [];
      let appointingProviders: GetCaseloadQueryResponse[] = [];
      if (isActiveClient && ba.serviceProviderId && (roles.isApprover || roles.isProxyDataEntry)) {
        try {
          if (roles.isApprover) {
            supervisedProviders = await API_SERVICEPROVIDERS_BACKEND
              .getCaseload({
                serviceProviderId: ba.serviceProviderId,
                caseloadType: CaseloadType.NUMBER_1,
              });
          }
          if (roles.isProxyDataEntry) {
            appointingProviders = await API_SERVICEPROVIDERS_BACKEND
              .getCaseload({
                serviceProviderId: ba.serviceProviderId,
                caseloadType: CaseloadType.NUMBER_2,
              });
          }
        } catch {
          // Non-critical
        }
      }

      return {
        client: ba.client ? { id: ba.client.id, name: ba.client.name, state: ba.client.state } : undefined,
        authorizedDistricts: districts.map((d) => ({ id: d.id, name: d.name })),
        ...roles,
        serviceProviderProfile: ba.serviceProviderId
          ? {
              id: ba.serviceProviderId,
              firstName: spRecord?.user?.firstName,
              lastName: spRecord?.user?.lastName,
              email: spRecord?.user?.emailAddress,
            }
          : undefined,
        supervisedServiceProviders: supervisedProviders.map((sp) => ({
          id: sp.id,
          firstName: sp.firstName,
          lastName: sp.lastName,
        })),
        appointingServiceProviders: appointingProviders.map((ap) => ({
          id: ap.id,
          firstName: ap.firstName,
          lastName: ap.lastName,
        })),
      } as OldClientAssignment;
    }),
  );
}

function buildServiceProviderTypes(
  backendTypes: ProviderTypeRecord[],
): OldServiceProviderType[] {
  return backendTypes.map((t) => ({
    id: t.id != null ? (t.id as any) : undefined,
    name: t.name,
  }));
}

function buildElectronicSignatures(
  backendSignatures: GetElectronicSignaturesResponse[],
): OldElectronicSignature[] {
  return backendSignatures.map((sig) => ({
    statementType: sig.statementType as any,
    isSigned: sig.isSigned,
    signedOnDateLocal:
      sig.signedOnDateLocal instanceof Date
        ? sig.signedOnDateLocal.toISOString()
        : sig.signedOnDateLocal,
    signedOnDateUtc:
      sig.signedOnDateUtc instanceof Date
        ? sig.signedOnDateUtc.toISOString()
        : sig.signedOnDateUtc,
    signedByFullName: sig.signedByFullName,
    requestIpAddress: sig.requestIpAddress,
  } as any as OldElectronicSignature));
}
